import React, { useRef } from 'react'
import type { TFunction } from 'i18next'
import { MetricCard } from '../../../../atoms/MetricCard'
import { TooltipMetricExplanation } from '../../../../TooltipMetricExplanation'
import { ChartHeader } from '../../../../molecules/ChartComponents'
import { downloadChartAsPng, sanitizeFilename } from '../../../../../utils'
import { type DemographicAnalysis } from '../../demographicsService'
import {
  PopulationPyramidChart,
  type PopulationPyramidChartRef,
} from '../charts/PopulationPyramidChart'
import {
  TabContentContainer,
  MetricsGrid,
  ChartContainer,
} from '../../DemographicsTab.styles'

export interface OverviewTabProps {
  data: DemographicAnalysis | null
  t: TFunction
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ data, t }) => {
  const populationPyramidChartRef = useRef<PopulationPyramidChartRef>(null)

  if (!data) return null

  const { current } = data

  // Calculate key metrics
  const foreignPercentage = Math.round(
    (current.total_population_foreign / current.total_population) * 100
  )
  const averageHouseholdSize =
    Math.round(
      (current.total_population / current.total_households_number) * 10
    ) / 10
  const ageMedian = calculateMedianAge(current)

  // Calculate CAGR (Compound Annual Growth Rate) over 10 years
  const cagr10Year =
    (Math.pow(1 + current.total_population_evolution_10_year, 0.1) - 1) * 100

  function calculateMedianAge(data: typeof current): number {
    // Approximate median age based on age group distributions
    const ageGroups = [
      { min: 0, max: 19, count: data.total_population_0_19 },
      { min: 20, max: 39, count: data.total_population_20_39 },
      { min: 40, max: 64, count: data.total_population_40_64 },
      { min: 65, max: 79, count: data.total_population_65_79 },
      { min: 80, max: 100, count: data.total_population_80_plus },
    ]

    const halfPopulation = data.total_population / 2
    let runningTotal = 0

    for (const group of ageGroups) {
      runningTotal += group.count
      if (runningTotal >= halfPopulation) {
        // Linear interpolation within the age group
        const excessCount = runningTotal - halfPopulation
        const positionInGroup = (group.count - excessCount) / group.count
        return Math.round(group.min + (group.max - group.min) * positionInGroup)
      }
    }

    return 42 // Fallback
  }

  const getGrowthDisplayValue = (evolution: number): string => {
    const percentage = Math.abs(evolution * 100)
    const sign = evolution >= 0 ? '+' : '-'
    return `${sign}${percentage.toFixed(1)}%`
  }

  const getGrowthVariant = (
    evolution: number
  ): 'success' | 'warning' | 'danger' | 'default' => {
    if (evolution > 0.05) return 'success'
    if (evolution > 0) return 'default'
    if (evolution > -0.05) return 'warning'
    return 'danger'
  }

  return (
    <TabContentContainer>
      {/* Key Metrics */}
      <MetricsGrid>
        <MetricCard
          title={t('metrics.totalPopulation.title')}
          value={current.total_population.toLocaleString()}
          subtitle={t('metrics.totalPopulation.subtitle')}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.totalPopulation.title')}
              shortDescription={t('tooltips.totalPopulation.shortDescription')}
              calculation={t('tooltips.totalPopulation.calculation')}
              importance={t('tooltips.totalPopulation.importance')}
              limitations={t('tooltips.totalPopulation.limitations')}
              source={t('tooltips.totalPopulation.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.populationGrowth.title')}
          value={getGrowthDisplayValue(
            current.total_population_evolution_10_year
          )}
          subtitle={t('metrics.populationGrowth.subtitle')}
          variant={getGrowthVariant(current.total_population_evolution_10_year)}
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.populationGrowth.title')}
              shortDescription={t('tooltips.populationGrowth.shortDescription')}
              calculation={t('tooltips.populationGrowth.calculation')}
              importance={t('tooltips.populationGrowth.importance')}
              limitations={t('tooltips.populationGrowth.limitations')}
              source={t('tooltips.populationGrowth.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.annualGrowthRate.title')}
          value={`${cagr10Year >= 0 ? '+' : ''}${cagr10Year.toFixed(2)}%`}
          subtitle={t('metrics.annualGrowthRate.subtitle')}
          variant={getGrowthVariant(current.total_population_evolution_10_year)}
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.compoundAnnualGrowthRate.title')}
              shortDescription={t(
                'tooltips.compoundAnnualGrowthRate.shortDescription'
              )}
              calculation={t('tooltips.compoundAnnualGrowthRate.calculation')}
              importance={t('tooltips.compoundAnnualGrowthRate.importance')}
              limitations={t('tooltips.compoundAnnualGrowthRate.limitations')}
              source={t('tooltips.compoundAnnualGrowthRate.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.medianAge.title')}
          value={t('metrics.medianAge.years', { age: ageMedian })}
          subtitle={t('metrics.medianAge.subtitle')}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.medianAge.title')}
              shortDescription={t('tooltips.medianAge.shortDescription')}
              calculation={t('tooltips.medianAge.calculation')}
              importance={t('tooltips.medianAge.importance')}
              limitations={t('tooltips.medianAge.limitations')}
              source={t('tooltips.medianAge.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.foreignResidents.title')}
          value={`${foreignPercentage}%`}
          subtitle={t('metrics.foreignResidents.subtitle', {
            count: current.total_population_foreign.toLocaleString(),
          })}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.foreignResidents.title')}
              shortDescription={t('tooltips.foreignResidents.shortDescription')}
              calculation={t('tooltips.foreignResidents.calculation')}
              importance={t('tooltips.foreignResidents.importance')}
              limitations={t('tooltips.foreignResidents.limitations')}
              source={t('tooltips.foreignResidents.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.householdSize.title')}
          value={t('metrics.averageHouseholdSize.people', {
            count: averageHouseholdSize,
          })}
          subtitle={t('metrics.householdSize.subtitle', {
            count: current.total_households_number.toLocaleString(),
          })}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.averageHouseholdSize.title')}
              shortDescription={t(
                'tooltips.averageHouseholdSize.shortDescription'
              )}
              calculation={t('tooltips.averageHouseholdSize.calculation')}
              importance={t('tooltips.averageHouseholdSize.importance')}
              limitations={t('tooltips.averageHouseholdSize.limitations')}
              source={t('tooltips.averageHouseholdSize.source')}
            />
          }
        />
      </MetricsGrid>

      {/* Population Pyramid Chart */}
      <ChartContainer>
        <ChartHeader
          title={t('charts.populationDistribution.title')}
          subtitle={t('charts.populationDistribution.subtitle')}
          onDownload={() => {
            const dataUrl = populationPyramidChartRef.current?.exportChart()
            downloadChartAsPng(
              dataUrl,
              sanitizeFilename(t('charts.populationDistribution.title'))
            )
          }}
          downloadLabel={t('common.downloadChart')}
        />
        <PopulationPyramidChart
          ref={populationPyramidChartRef}
          data={current}
          t={t}
        />
      </ChartContainer>
    </TabContentContainer>
  )
}
